import { z } from 'zod';
import type React from 'react';

// Apoio stateless (régua de 400 linhas) — extraído sem mudança de comportamento.
export const TYPE_LABELS: Record<string, string> = {
  PERSON_OBLIGATION: 'Obrigação pessoal',
  HOUSE_OBLIGATION: 'Obrigação da casa',
  TIME_CYCLE: 'Ciclo temporal',
  ANNUAL_OBLIGATION: 'Obrigação anual',
  ROLE_OBLIGATION: 'Obrigação de cargo',
  INITIATION_RELATED: 'Relacionada a iniciação',
  ANCESTOR_RELATED: 'Relacionada a ancestral',
  CALENDAR_RELATED: 'Relacionada ao calendário',
  EMERGENCY: 'Emergência',
  OTHER: 'Outro',
};

export const STATUS_LABELS: Record<string, string> = {
  PLANNED: 'Planejada',
  IN_PREPARATION: 'Em preparação',
  PENDING_DECISION: 'Aguardando decisão',
  PENDING_MATERIALS: 'Aguardando materiais',
  PENDING_FINANCE: 'Aguardando finanças',
  PENDING_PEOPLE: 'Aguardando pessoas',
  SCHEDULED: 'Agendada',
  COMPLETED: 'Concluída',
  POSTPONED: 'Adiada',
  CANCELLED: 'Cancelada',
  ARCHIVED: 'Arquivada',
  RESTRICTED_ARCHIVED: 'Arquivada restrita',
};

export const STATUS_VARIANTS: Record<string, string> = {
  PLANNED: 'default',
  IN_PREPARATION: 'info',
  PENDING_DECISION: 'warning',
  PENDING_MATERIALS: 'warning',
  PENDING_FINANCE: 'warning',
  PENDING_PEOPLE: 'warning',
  SCHEDULED: 'info',
  COMPLETED: 'success',
  POSTPONED: 'danger',
  CANCELLED: 'danger',
  ARCHIVED: 'default',
  RESTRICTED_ARCHIVED: 'danger',
};

export const VISIBILITY_LABELS: Record<string, string> = {
  PUBLIC: 'Público',
  INTERNAL: 'Interno',
  RESTRICTED: 'Restrito',
};

export const VISIBILITY_VARIANTS: Record<string, string> = {
  PUBLIC: 'success',
  INTERNAL: 'info',
  RESTRICTED: 'danger',
};

export const createFormSchema = z.object({
  title: z.string().min(1, 'Título é obrigatório').max(255),
  type: z.string().min(1, 'Tipo é obrigatório'),
  visibility: z.string(),
  description: z.string().optional(),
  expectedDate: z.string().optional(),
  restrictedNotes: z.string().optional(),
  eventId: z.string().optional(),
  responsibleIds: z.array(z.string()).optional(),
});

export type CreateForm = z.infer<typeof createFormSchema>;

export function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        padding: '0.625rem 0.75rem',
        textAlign: 'left',
        fontSize: '0.75rem',
        fontWeight: 600,
        color: 'var(--muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      }}
    >
      {children}
    </th>
  );
}

export function Td(props: React.HTMLAttributes<HTMLTableCellElement>) {
  return (
    <td style={{ padding: '0.625rem 0.75rem', fontSize: '0.875rem', ...props.style }}>
      {props.children}
    </td>
  );
}

export function PaginationButton({
  active,
  disabled,
  onClick,
  label,
}: {
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      style={{
        minWidth: 32,
        height: 32,
        padding: '0 0.5rem',
        border: active ? '1px solid var(--green)' : '1px solid var(--line)',
        borderRadius: 10,
        background: active ? 'var(--green)' : '#fff',
        color: active ? '#fff' : 'var(--muted)',
        fontSize: '0.8rem',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {label}
    </button>
  );
}

export function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.8rem' }}>
      <span style={{ color: 'var(--ink)', fontWeight: 600 }}>{label}</span>
      {children}
      {error && <span style={{ color: 'var(--danger)', fontSize: '0.7rem' }}>{error}</span>}
    </label>
  );
}

export function Skeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div
        style={{
          background: '#fff',
          borderRadius: 14,
          border: '1px solid var(--line)',
          padding: '1.25rem 1.5rem',
        }}
      >
        {Array.from({ length: 5 }, (_, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              gap: '2rem',
              padding: '0.5rem 0',
              borderBottom: i < 4 ? '1px solid var(--line-soft)' : 'none',
            }}
          >
            <div
              style={{ width: 200, height: 14, background: 'var(--line-soft)', borderRadius: 8 }}
            />
            <div
              style={{ width: 140, height: 14, background: 'var(--line-soft)', borderRadius: 8 }}
            />
            <div
              style={{ width: 100, height: 14, background: 'var(--line-soft)', borderRadius: 8 }}
            />
            <div
              style={{ width: 80, height: 14, background: 'var(--line-soft)', borderRadius: 8 }}
            />
            <div
              style={{ width: 80, height: 14, background: 'var(--line-soft)', borderRadius: 8 }}
            />
            <div
              style={{ width: 40, height: 14, background: 'var(--line-soft)', borderRadius: 8 }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export const inputCss: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.6rem',
  border: '1px solid var(--line)',
  borderRadius: 10,
  fontSize: '0.85rem',
  boxSizing: 'border-box',
  outline: 'none',
};

export const primaryBtn: React.CSSProperties = {
  background: 'var(--green)',
  color: '#fff',
  border: 'none',
  borderRadius: 999,
  padding: '0.45rem 1rem',
  fontSize: '0.8rem',
  fontWeight: 600,
  cursor: 'pointer',
};

export const secondaryBtn: React.CSSProperties = {
  background: '#fff',
  color: 'var(--muted)',
  border: '1px solid var(--line)',
  borderRadius: 999,
  padding: '0.45rem 1rem',
  fontSize: '0.8rem',
  cursor: 'pointer',
};

export const modalOverlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'var(--overlay)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 100,
};

export const modalStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 14,
  padding: '1.5rem',
  minWidth: 460,
  maxWidth: 520,
  maxHeight: '90vh',
  overflow: 'auto',
};
